import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';

const InputDialog = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  title,
  message,
  inputLabel,
  inputPlaceholder,
  submitText = 'Submit',
  cancelText = 'Cancel',
  required = false,
  loading = false,
  defaultValue = ''
}) => {
  const [value, setValue] = useState(defaultValue);
  const [error, setError] = useState('');

  // Reset value when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setValue(defaultValue);
      setError('');
    }
  }, [isOpen, defaultValue]);

  // Handle ESC key to close
  useEffect(() => {
    if (!isOpen) return;
    
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !loading) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, loading, onClose]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (required && !value.trim()) {
      setError('This field is required');
      return;
    }
    onSubmit(value);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm" 
        onClick={loading ? undefined : onClose} 
      />
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-start gap-4 mb-4">
          <Icon name="Info" size={24} className="text-blue-600" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
            {message && <p className="text-sm text-gray-600">{message}</p>}
          </div>
        </div>
        <div className="mb-4">
          <Input
            label={inputLabel}
            placeholder={inputPlaceholder}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setError('');
            }}
            error={error}
            required={required}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !loading) {
                handleSubmit();
              }
            }}
          />
        </div>
        <div className="flex gap-3">
          <Button 
            onClick={onClose} 
            variant="outline" 
            className="flex-1"
            disabled={loading}
          >
            {cancelText}
          </Button>
          <Button 
            onClick={handleSubmit} 
            className="flex-1" 
            loading={loading}
            disabled={loading}
          >
            {submitText}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InputDialog;
